import React from 'react';
import {DISCIPLINES_BY_ID, getAvailablePowers} from '../../data/disciplines';
import {CLANS_BY_ID} from '../../data/clans';
import {localized} from '../../data/traits';
import {getCreationDisciplines, getCreationDots, setCreationDots, togglePower, canAddPower} from '../../creation';
import {vtmT} from '../../i18n';
import {formatPool} from '../SheetParts';

const DOT_OPTIONS = [0, 1, 2];

const PowerPicker = ({sheet, change, disciplineId, lng}) => {
  const discipline = sheet.disciplines[disciplineId];
  if (!discipline) return null;
  const levels = Object.entries(sheet.disciplines).reduce((acc, [id, value]) => ({...acc, [id]: value.level}), {});
  const powers = discipline.powers || [];
  return (
    <fieldset className="vtmPowerPicker">
      <legend className="vtmFieldLabel">{vtmT('powersToChoose', {count: powers.length, max: discipline.level})}</legend>
      {getAvailablePowers(disciplineId, discipline.level, levels).map(power => {
        const checked = powers.includes(power.id);
        const blocked = !checked && !canAddPower(sheet, disciplineId, power.id);
        const id = `vtmPower-${power.id}`;
        return (
          <label key={power.id} htmlFor={id} className={`vtmPowerOption${blocked ? ' disabled' : ''}`}>
            <input
              id={id}
              type="checkbox"
              checked={checked}
              disabled={blocked}
              onChange={() => change(togglePower(sheet, disciplineId, power.id))}
            />
            <span className="vtmPowerOptionText">
              <span className="vtmPowerName">{power.level} · {localized(power.name, lng)}</span>
              <span className="vtmPowerMeta">
                {power.rouse ? vtmT('rouseCost', {value: power.rouse}) : vtmT('rouseFree')}
                {power.pool && ` · ${formatPool(power.pool, lng)}`}
                {power.amalgam && ` · ${vtmT('amalgamRequires', {discipline: localized(DISCIPLINES_BY_ID[power.amalgam.discipline]?.name, lng), level: power.amalgam.level})}`}
              </span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
};

export const DisciplinesStep = ({sheet, change, lng}) => {
  const clan = CLANS_BY_ID[sheet.clan];
  if (!clan) return <p className="vtmHint">{vtmT('chooseClanFirst')}</p>;
  const creationIds = getCreationDisciplines(sheet.clan);
  // Disciplines affichées : celles du clan + celle du prédateur si elle est hors clan
  const ids = [...new Set([...creationIds, ...(sheet.predatorDiscipline ? [sheet.predatorDiscipline] : [])])]
    .filter(id => creationIds.includes(id) || sheet.disciplines[id]);

  return (
    <div className="vtmDisciplineEditor">
      {clan.thinBlood && <p className="vtmHint">{vtmT('thinBloodDisciplines')}</p>}
      {ids.map(id => {
        const name = localized(DISCIPLINES_BY_ID[id]?.name, lng);
        const dots = getCreationDots(sheet, id);
        const canTakeDots = creationIds.includes(id);
        return (
          <section key={id} className="vtmDisciplineCard">
            <div className="vtmDisciplineCardHeader">
              <h3 className="vtmDisciplineName">{name}</h3>
              {sheet.predatorDiscipline === id && <span className="vtmBadge">{vtmT('predatorDot')}</span>}
              {canTakeDots && (
                <div className="vtmSegmented" role="radiogroup" aria-label={`${name} : ${vtmT('creationDots')}`}>
                  {DOT_OPTIONS.map(value => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={dots === value}
                      className={dots === value ? 'selected' : ''}
                      onClick={() => change(setCreationDots(sheet, id, value))}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <PowerPicker sheet={sheet} change={change} disciplineId={id} lng={lng} />
          </section>
        );
      })}
    </div>
  );
};
